import React, { useState, useEffect } from "react";
import { API } from "../../BlakeData";
import { PrintRecord } from "../../BlakeData/Types";
// import { useMediaQuery } from 'react-responsive';
import Measure, { ContentRect } from 'react-measure';

import "./style/main.scss";
import { LeftSide } from "./LeftSide";
import { RightSide } from "./RightSide";

import { Redirect } from 'react-router-dom'
import LoadingPage from "../LoadingPage";

interface GroupPageProps {
    location: {
        pathname: string
    },
    match: {
        params: {
            groupID: string,
            firstImage?: string,
            secondImage?: string,
            mode: string
        }
    }
}

// function Loading() {
//     return (
//         <div className="PageLoading">
//             Page loading
//         </div>
//     )
// }

export default React.memo(function GroupPage(props: GroupPageProps) {

    const groupID = props.match.params.groupID;
    const [prints, setPrints] = useState<Array<PrintRecord>>([]);
    const [stateURL, setStateURL] = useState<string>(props.location.pathname);
    const [redirect, setRedirect] = useState<boolean>(false);
    // const [selectedPrints, setSelectedPrints] = useState<Array<number>>([]);
    const [dimensions, setDimensions] = useState<{ width: number, height: number }>({ width: 0, height: 0 });
    // const [compareMode, setCompareMode] = useState<boolean>(prints.length > 1);

    const compareMode = props.match.params.mode === "compare";
    const quantizeMode = props.match.params.mode === "quantize";

    // Load all of the prints from the database
    useEffect(() => {
        // console.log("Loading prints for " + groupID)
        // const startTime = new Date().getTime();
        API.getPrintsByGroupID(groupID)
            .then((prints: Array<PrintRecord>) => {
                // this is slow in safari
                // const endTime = new Date().getTime();
                // console.log("Loaded prints for " + groupID, endTime - startTime)

                setPrints(prints);
            })
            .catch((e: any) => {
                console.log("caught when trying to load prints for " + groupID, e);
            })
            ;

    }, [groupID]);

    //
    // generate a list of selected prints from the route path   
    //
    const printIDs = prints.map((p: PrintRecord) => p.id);
    const printIDisValid = (printID: string) => printIDs.includes(printID);
    const selectedPrints: Array<number> = (function () {

        const { firstImage, secondImage } = props.match.params;
        let ret: Array<number> = [];
        const addImg = (img: string | undefined) => {
            if (!img) { return }
            const idx = printIDs.indexOf(img);
            if (idx < 0) { return }
            if (ret.includes(idx)) { return }
            if (ret.length < 2 && img) { ret.push(idx) }
        }
        addImg(firstImage);
        addImg(secondImage);
        return ret

    })();

    // only update the stateURL if it's a new one
    const setStateURLWrapper = (newURL: string) => {

        if (stateURL !== newURL) {
            setStateURL(newURL)
            setRedirect(true);
        }
    }

    /**
     *  Generate a stateful URL from the current compare mode and selected images
     */
    const makeURL = function (opts: {
        quantizeMode: boolean,
        compareMode: boolean,
        imgIDArray: Array<string | undefined>
    }) {
        const { quantizeMode, compareMode, imgIDArray } = opts;

        let imgIDs = imgIDArray.slice();

        if (!compareMode) { imgIDs = imgIDArray.slice(0, 1) }

        let modeString = "";
        if (compareMode) { modeString = "compare" }
        else if (quantizeMode) { modeString = "quantize" }
        else { modeString = "view" }

        let ret = `/group/${modeString}/${groupID}`;
        const addImgToURL = (imgID: string | undefined) => {
            if (!imgID) { return }
            if (!printIDisValid(imgID)) { return }
            ret += `/${imgID}`;
        }
        imgIDs.forEach(imgID => addImgToURL(imgID))
        return ret;
    };


    /**
     * set the compare mode only if it's different and update the URL
     * @param newCompareMode 
     */
    const setCompareModeWrapper = (newCompareMode: boolean) => {

        if (newCompareMode !== compareMode) {

            const url = makeURL({
                quantizeMode: quantizeMode,
                compareMode: newCompareMode,
                imgIDArray: [props.match.params.firstImage, props.match.params.secondImage]
            });
            setStateURLWrapper(url)

        } else {
        }
        // setSelectedPrintsWrapper(selectedPrints.slice(0,1));

    };

    // we're just going to force narrow to be true since redesign of UI
    // calls for only a single-column mode. this may not always be the case
    // const narrow = useMediaQuery({ query: '(max-width: 800px)' });
    const narrow = true;

    /**
     * set the selected prints only if they've changed
     * @param idxArr 
     */
    const setSelectedPrintsWrapper = (idxArr: Array<number>) => {

        // generate URL
        const arr = idxArr.map(idx => prints[idx].id)
        const newURL = makeURL({
            quantizeMode: quantizeMode,
            compareMode: compareMode,
            imgIDArray: arr
        });

        // setSelectedPrints(idxArr);
        setStateURLWrapper(newURL);
    }

    const setQuantizeModeWrapper = (newQuantizedMode: boolean) => {

        if (quantizeMode === newQuantizedMode) { return }
        setStateURLWrapper(
            makeURL({
                quantizeMode: newQuantizedMode,
                compareMode: false,
                imgIDArray: [props.match.params.firstImage, props.match.params.secondImage]
            })
        )

    }

    const sideWidth = narrow ? dimensions.width : dimensions.width / 2;

    // create the "left side", which is the picker stuff.
    // this was named before we decided on a one-column layout
    // so it's not split left-right
    const pickerSide = <LeftSide
        width={sideWidth}
        showMicropalette={!narrow}
        prints={prints}
        selectedPrints={selectedPrints}
        setSelectedPrints={setSelectedPrintsWrapper}
        groupID={props.match.params.groupID}
        compareMode={compareMode}
    />;

    // create the viewer side. see above for why the name is "RightSide"
    const viewerSide = <RightSide
        width={sideWidth}
        height={dimensions.height}
        quantizeMode={quantizeMode}
        setQuantizeMode={setQuantizeModeWrapper}
        prints={selectedPrints.map((idx) => { return prints[idx]; })}
        compareMode={compareMode}
        setCompareMode={setCompareModeWrapper}
    />;

    // in narrow mode, the picker stuff is at the bottom, 
    // in wide, two-column mode, the picker stuff is on the left
    // so we have to compute the order on the fly
    const firstSide = narrow ? viewerSide : pickerSide;
    const secondSide = narrow ? pickerSide : viewerSide;

    // if we're supposed to redirect, redirect instead of
    // rendering the real view
    if (redirect) {
        const currentURL = props.location.pathname;

        if (currentURL !== stateURL) {
            // console.log("Redirecting GroupPage");

            return <Redirect to={stateURL} />
        } else {
            setRedirect(false);
        }
    }

    if (prints.length < 1){
        return <LoadingPage/>
    }


        return (
            <Measure
                bounds
                onResize={(contentRect: ContentRect) => {
                    let { width, height } = contentRect.bounds || { width: 0, height: 0 };
                    setDimensions({
                        width,
                        height
                    });

                }}
            >
                {({ measureRef }) => (
                    <main className="GroupPage" ref={measureRef}>
                        <div className={narrow ? "split-vertical" : "split-horizontal"}>
                            <div className={narrow ? "top half" : "left half"}>
                                {/* <h3>Group page for {groupID}</h3> */}
                                {firstSide}
                            </div>
                            <div className={narrow ? "bottom half" : "right half"}>
                                {secondSide}
                            </div>
                        </div>
                    </main>
                )}

            </Measure>
        );
});
